'use es6';

import { useCallback } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List } from 'foundations-components/transitional/components/List';
import { TableHoverCell } from 'foundations-components/transitional/components/Table';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { SCHEDULE_FREQUENCY, getFormattedDayLabels, getFormattedTimeLabels } from '../../../lib/schedulingLogic';
import { SHARE_SLACK, SHARE_EMAIL } from '../../shareAsset/shareTypes';
import { useShowDialog } from 'reporting-action-components/dialog/ducks';
import { EMAIL_DASHBOARD_DIALOG, SHARE_VIA_DIALOG } from '../../../dialog/dialogTypes';
import { useState } from 'react';
import ChangeRecurringAssetOwnerModal from './ChangeRecurringAssetOwnerModal';
import DeleteEmailConfirmModal from './DeleteEmailConfirmModal';
import useEditRecurringEmailProblem from './useEditRecurringEmailProblem';
import { StatusTag } from 'foundations-components/transitional/components/StatusTag';
import { useTracker } from '../../../utils/hooks/useTracker';
import { EVENTS } from '../../../utils/usageTracker';
import { getUserLabel } from '../../../lib/user-logic';
import I18n from 'I18n';
import { requiredReportOrDashboardPropCheck } from '../../shared/sharedPropValidation';
import { DASHBOARD_OBJECT_TYPE } from 'dashboard-lib/public/emails/emails';
import { DEFAULT_EXPORT_TYPE } from '../../shared/emailLogic';
import { useShareThirdPartyApp } from '../../../data/hooks/useShareThirdPartyApp';
import { noop } from '../../shared/noop';
import { isAcceptanceTestPortal } from '../../../utils/windowUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const UNSUBSCRIBED_SLACK = 'NO_VALID_RECIPIENT';
const SCHEDULE_LABEL_TRANS_KEYS = {
  [SCHEDULE_FREQUENCY.DAILY]: 'reporting-action-components.actions.manageRecurringEmails.dialog.table.newScheduleFormat.DAILY',
  [SCHEDULE_FREQUENCY.WEEKLY]: 'reporting-action-components.actions.manageRecurringEmails.dialog.table.newScheduleFormat.WEEKLY',
  [SCHEDULE_FREQUENCY.MONTHLY]: 'reporting-action-components.actions.manageRecurringEmails.dialog.table.newScheduleFormat.MONTHLY'
};
const SHARE_ASSET_WARNING_MESSAGES = {
  NO_VALID_RECIPIENT: 'reporting-action-components.actions.manageRecurringEmails.dialog.warning.NO_VALID_RECIPIENT'
};
const SHARE_ASSET_ERROR_MESSAGES = {
  DEACTIVATED_USER: 'reporting-action-components.actions.manageRecurringEmails.dialog.error.DEACTIVATED_USER',
  MISSING_REPORTING_ACCESS_SCOPE: 'reporting-action-components.actions.manageRecurringEmails.dialog.error.MISSING_REPORTING_ACCESS_SCOPE',
  PARTITIONING_ERROR: 'reporting-action-components.actions.manageRecurringEmails.dialog.error.PARTITIONING_ERROR'
};
const assetSpecificTrackingPropsPropDefault = {};
const ScheduledEmailRow = ({
  appUsageTracker,
  createAndOwnUsers,
  createAndOwnUsersLabels,
  onEditSuccess = noop,
  report,
  dashboard,
  schedule,
  objectType,
  assetSpecificTrackingProps = assetSpecificTrackingPropsPropDefault,
  isImprovedDBFilterOptIn = false,
  currentDialog
}) => {
  var _JSON$parse;
  const usageTracker = useTracker(appUsageTracker);
  const showDialog = useShowDialog();
  const scheduleId = schedule.id;
  const scheduleFrequency = schedule.scheduleFrequency;
  const isUnsubscribedSlack = currentDialog === SHARE_SLACK && !schedule.recipientDestination;
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const lowerCasedDialog = currentDialog.toLowerCase();
  const {
    disabledMessage: editRecurringEmailDisabledMessage
  } = useEditRecurringEmailProblem({
    report,
    dashboard,
    schedule
  });
  const appId = (_JSON$parse = JSON.parse(schedule.recipientDestination)) === null || _JSON$parse === void 0 ? void 0 : _JSON$parse.appId;
  const [showChangeOwnerModal, setShowChangeOwnerModal] = useState(false);
  const {
    data: thirdPartyApp
  } = useShareThirdPartyApp(appId);
  const appName = (thirdPartyApp === null || thirdPartyApp === void 0 ? void 0 : thirdPartyApp.name) || '';
  const displayDataExport = currentDialog === SHARE_EMAIL;
  const handleClickEdit = () => {
    if (usageTracker) {
      usageTracker.track(EVENTS.startEditRecurringShareAssetFlow, Object.assign({
        object: lowerCasedDialog,
        actionType: 'edit',
        action: 'edit-schedule',
        interactionSource: 'dialog',
        assetType: objectType.toLowerCase()
      }, assetSpecificTrackingProps));
    }
    showDialog({
      dialogType: objectType === DASHBOARD_OBJECT_TYPE ? EMAIL_DASHBOARD_DIALOG : SHARE_VIA_DIALOG,
      dialogProps: {
        appUsageTracker,
        onEditSuccess,
        report,
        dashboard,
        scheduledSharedAsset: schedule,
        isImprovedDBFilterOptIn
      }
    });
  };
  const renderFileCell = () => {
    const dataExportType = schedule.exportType.toLowerCase() || DEFAULT_EXPORT_TYPE.NONE.toLowerCase();
    return /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `reporting-action-components.actions.emailReport.dialog.exportType.${dataExportType}`
      })
    });
  };
  const renderOwnerCell = () => {
    const emailOwnerId = schedule.ownerId;
    const owner = createAndOwnUsers.get(String(emailOwnerId));
    const ownerLabel = owner ? getUserLabel({
      user: owner
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.manageRecurringEmails.dialog.table.deactivatedUser"
    });
    return /*#__PURE__*/_jsx("td", {
      children: ownerLabel
    });
  };
  const renderChangeOwnerButton = () => {
    return /*#__PURE__*/_jsx(Tooltip, {
      disabled: !editRecurringEmailDisabledMessage,
      title: editRecurringEmailDisabledMessage,
      placement: "left bottom",
      children: /*#__PURE__*/_jsx(Button, {
        size: "extra-small",
        use: "tertiary-light",
        onClick: () => {
          if (usageTracker) {
            usageTracker.track(EVENTS.startEditRecurringShareAssetFlow, Object.assign({
              object: lowerCasedDialog,
              actionType: 'edit',
              action: 'change-owner',
              interactionSource: 'actions-dropdown',
              assetType: dashboard ? 'dashboard' : 'report'
            }, assetSpecificTrackingProps));
          }
          setShowChangeOwnerModal(true);
        },
        disabled: !!editRecurringEmailDisabledMessage,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.manageRecurringEmails.dialog.table.actionsDropdown.changeOwnerAction.label"
        })
      })
    });
  };
  const renderDeleteButton = () => {
    return /*#__PURE__*/_jsx(Tooltip, {
      disabled: !editRecurringEmailDisabledMessage,
      title: editRecurringEmailDisabledMessage,
      placement: "left bottom",
      children: /*#__PURE__*/_jsx(Button, {
        size: "extra-small",
        use: "tertiary-light",
        disabled: !!editRecurringEmailDisabledMessage,
        onClick: () => {
          if (usageTracker) {
            usageTracker.track(EVENTS.startEditRecurringShareAssetFlow, Object.assign({
              object: lowerCasedDialog,
              actionType: 'edit',
              action: 'delete',
              interactionSource: 'actions-dropdown',
              assetType: dashboard ? 'dashboard' : 'report'
            }, assetSpecificTrackingProps));
            setShowConfirmDeleteModal(true);
          }
        },
        "data-test-id": "recurring-email-delete-action",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.manageRecurringEmails.dialog.table.actionsDropdown.deleteAction.label"
        })
      })
    });
  };
  const renderEmailStatus = () => {
    const errorCode = isUnsubscribedSlack ? UNSUBSCRIBED_SLACK : schedule.dashboardEmailError;
    const errorMessage = SHARE_ASSET_ERROR_MESSAGES[errorCode];
    const warningMessage = SHARE_ASSET_WARNING_MESSAGES[errorCode];
    return errorCode && /*#__PURE__*/_jsx(StatusTag, {
      use: warningMessage ? 'warning' : 'error',
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: errorMessage || warningMessage || 'reporting-action-components.actions.manageRecurringEmails.dialog.error.general'
      })
    });
  };
  const renderAppName = useCallback(() => {
    return /*#__PURE__*/_jsx("td", {
      children: appName
    });
  }, [appName]);
  const renderNicknameCell = () => {
    const hoverContentProps = isAcceptanceTestPortal() ? {
      open: true
    } : {};
    return /*#__PURE__*/_jsxs(TableHoverCell, {
      hoverContent: /*#__PURE__*/_jsx(Dropdown, {
        buttonSize: "extra-small",
        buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.manageRecurringEmails.dialog.table.actionsDropdown.label"
        }),
        "data-test-id": "recurring-email-actions-dropdown",
        children: /*#__PURE__*/_jsxs(List, {
          wrapChildren: true,
          children: [renderChangeOwnerButton(schedule), renderDeleteButton(schedule)]
        })
      }),
      hoverContentProps: hoverContentProps,
      children: [/*#__PURE__*/_jsx(Tooltip, {
        disabled: !editRecurringEmailDisabledMessage,
        title: editRecurringEmailDisabledMessage,
        placement: "left bottom",
        children: /*#__PURE__*/_jsx(Button, {
          use: "link",
          onClick: () => {
            handleClickEdit();
          },
          disabled: !!editRecurringEmailDisabledMessage,
          children: /*#__PURE__*/_jsx(TruncateString, {
            children: schedule.nickname || schedule.subject
          })
        })
      }), /*#__PURE__*/_jsx("br", {}), renderEmailStatus()]
    });
  };
  const renderChangeOwnerModal = () => showChangeOwnerModal && /*#__PURE__*/_jsx(ChangeRecurringAssetOwnerModal, {
    appUsageTracker: appUsageTracker,
    closeDialog: () => setShowChangeOwnerModal(false),
    schedule: schedule,
    createAndOwnUsersLabels: createAndOwnUsersLabels,
    onEditSuccess: onEditSuccess,
    assetSpecificTrackingProps: assetSpecificTrackingProps,
    objectType: objectType,
    currentDialog: currentDialog.toLowerCase()
  });
  const renderConfirmDeleteModal = () => showConfirmDeleteModal && /*#__PURE__*/_jsx(DeleteEmailConfirmModal, {
    appUsageTracker: appUsageTracker,
    closeDialog: () => {
      setShowConfirmDeleteModal(false);
    },
    scheduleId: scheduleId,
    onEditSuccess: onEditSuccess,
    assetSpecificTrackingProps: assetSpecificTrackingProps,
    objectType: objectType,
    currentDialog: currentDialog
  });
  const renderFrequencyCell = () => /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: 'reporting-action-components.actions.manageRecurringEmails.dialog.table.timeOfDay',
      options: getFormattedTimeLabels({
        scheduleAtHour: schedule.scheduledAtHour,
        isUtc: schedule.isUtc
      })
    })
  });
  const renderScheduleCell = () => /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsx(TruncateString, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: SCHEDULE_LABEL_TRANS_KEYS[scheduleFrequency],
        options: {
          days: I18n.formatList(getFormattedDayLabels({
            scheduleFrequency: schedule.scheduleFrequency,
            scheduleOnDays: schedule.scheduledOnDays,
            isUtc: schedule.isUtc
          }))
        }
      })
    })
  });
  return /*#__PURE__*/_jsxs("tr", {
    children: [currentDialog !== SHARE_EMAIL && renderAppName(), renderNicknameCell(schedule), renderFrequencyCell(), renderScheduleCell(), renderOwnerCell(schedule), displayDataExport && renderFileCell(), renderChangeOwnerModal(), renderConfirmDeleteModal()]
  }, `schedule-${scheduleId}`);
};
ScheduledEmailRow.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  report: requiredReportOrDashboardPropCheck,
  dashboard: requiredReportOrDashboardPropCheck,
  schedule: PropTypes.object.isRequired,
  createAndOwnUsers: PropTypes.object.isRequired,
  createAndOwnUsersLabels: PropTypes.array.isRequired,
  onEditSuccess: PropTypes.func,
  objectType: PropTypes.string.isRequired,
  assetSpecificTrackingProps: PropTypes.object,
  isImprovedDBFilterOptIn: PropTypes.bool,
  currentDialog: PropTypes.string
};
export default ScheduledEmailRow;